import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';
import { MEDIA } from '@/data/plotData';

const RENDER_GALLERY = [
  {
    src: MEDIA.renderOblique,
    en: { label: 'Configuration A — Elevated Oblique View', desc: 'Full community layout with dual entrance/exit gates, flat RCC terraces, road layout, and the park/clubhouse structure aligned to the North.' },
    ta: { label: 'அமைப்பு A — உயர்நிலை பக்கவாட்டு காட்சி', desc: 'இரட்டை நுழைவு/வெளியேற்ற வாயில்கள், தட்டையான RCC மொட்டை மாடிகள், சாலை அமைப்பு மற்றும் வடக்கில் பூங்கா/கிளப் ஹவுஸ் அமைப்பு உடன் முழு சமூக அமைப்பு.' },
  },
  {
    src: MEDIA.renderFront,
    en: { label: 'Configuration B — Front Perspective View', desc: 'Elevated side oblique perspective highlighting uniform spacing, modular plot boundaries, and structural setback clearances across the development rows.' },
    ta: { label: 'அமைப்பு B — முன்பக்க காட்சி', desc: 'சீரான இடைவெளி, தொகுதி பிளாட் எல்லைகள் மற்றும் வளர்ச்சி வரிசைகள் முழுவதும் கட்டமைப்பு பின்வாங்கல் இடைவெளிகளை முன்னிலைப்படுத்தும் உயர்நிலை பக்கவாட்டு காட்சி.' },
  },
];

export default function PlotConfigurations() {
  const { lang } = useLanguage();
  const t = translations[lang].configs;
  const [lightbox, setLightbox] = useState(null);
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 400, behavior: 'smooth' });
  };

  return (
    <section data-testid="plot-configurations" className="py-16 sm:py-24 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-4"
        >
          <span className="text-[#D48C57] text-xs font-medium tracking-[0.2em] uppercase">
            {t.sectionTag}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A2520] mt-3 tracking-tight">
            {t.title}
          </h2>
        </motion.div>

        {/* Disclaimer */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="text-[#5C6A61] text-xs sm:text-sm leading-relaxed mb-10 max-w-3xl italic border-l-2 border-[#D48C57] pl-4"
        >
          {t.disclaimer}
        </motion.p>

        {/* Desktop: Two-column grid */}
        <div className="hidden sm:grid sm:grid-cols-2 gap-4">
          {RENDER_GALLERY.map((item, i) => {
            const content = item[lang];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                data-testid={`render-card-${i}`}
                className="group relative rounded-2xl overflow-hidden border border-[#E5DFD3] bg-white cursor-pointer"
                onClick={() => setLightbox(item.src)}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={item.src}
                    alt={content.label}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full p-3">
                    <Maximize2 className="w-5 h-5 text-[#1A2520]" />
                  </div>
                </div>
                {/* Label */}
                <div className="p-4">
                  <p className="font-serif text-sm font-bold text-[#1A2520]">{content.label}</p>
                  <p className="text-[#5C6A61] text-xs mt-1 leading-relaxed">{content.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Mobile: Horizontal swiper */}
        <div className="sm:hidden relative">
          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {RENDER_GALLERY.map((item, i) => {
              const content = item[lang];
              return (
                <div
                  key={i}
                  className="flex-shrink-0 w-[85vw] snap-center rounded-2xl overflow-hidden border border-[#E5DFD3] bg-white"
                  onClick={() => setLightbox(item.src)}
                  data-testid={`render-card-mobile-${i}`}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={item.src} alt={content.label} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-3">
                    <p className="font-serif text-sm font-bold text-[#1A2520]">{content.label}</p>
                    <p className="text-[#5C6A61] text-[11px] mt-1 leading-relaxed">{content.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Nav arrows */}
          <div className="flex justify-center gap-3 mt-2">
            <button onClick={() => scroll(-1)} className="w-8 h-8 rounded-full bg-[#F4F1EA] border border-[#E5DFD3] flex items-center justify-center">
              <ChevronLeft className="w-4 h-4 text-[#5C6A61]" />
            </button>
            <button onClick={() => scroll(1)} className="w-8 h-8 rounded-full bg-[#F4F1EA] border border-[#E5DFD3] flex items-center justify-center">
              <ChevronRight className="w-4 h-4 text-[#5C6A61]" />
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="sm:max-w-5xl p-1 bg-black/95 border-0 rounded-2xl">
          {lightbox && (
            <img src={lightbox} alt="Plot configuration render" className="w-full h-auto rounded-xl" />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
